import { useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';
import { classifyPhoto, type GalleryImage } from './_galleryShared';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Static slot group definitions (contentSchema requires inline literals)
// ============================================================================

// Note: no defaultField / sourceBinding — a fixed `photo_gallery[N]` index
// would just pick whatever photo happens to sit at that position for the
// property, which is rarely the community/apartment shot we want here. Instead
// the component reads the full photo_gallery below and uses `classifyPhoto`
// to pick the first photo whose title identifies it as community or apartment.
// The editable slot group still lets site owners swap in a specific image.
const communityImageDef: SlotGroupDefinition = {
  label: 'Community Amenities Image',
  description:
    'Cycles photos from the property photo gallery whose title identifies them as community. Site owners can override with a specific image.',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
};

const apartmentImageDef: SlotGroupDefinition = {
  label: 'Apartment Features Image',
  description:
    'Cycles photos from the property photo gallery whose title identifies them as apartment. Site owners can override with a specific image.',
  fields: {
    image: { label: 'Image', type: 'image' },
    alt: { label: 'Alt Text', type: 'text' },
  },
};

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Apartment CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Image carousel — cycles the classified community/apartment photos with
// prev/next arrows that appear on hover or keyboard focus. When a site owner
// has overridden the slot with a specific image, the carousel collapses to
// that single image (no arrows), since a manual pick shouldn't be re-shuffled
// out from under the editor.
//
// Rendered as the children of an <EditableSlotGroup> — keeping the wrapper at
// the call site lets the schema linter statically match each groupPath.
// ============================================================================

interface AmenitiesImageCarouselProps {
  photos: GalleryImage[];
  /** Slot-group override values from useSlotGroup — null when unset. */
  override: { image?: string | null; alt?: string | null };
  /** Fallback alt if neither override nor photo carry one (e.g. subtitle text). */
  fallbackAlt: string;
}

function AmenitiesImageCarousel({
  photos,
  override,
  fallbackAlt,
}: AmenitiesImageCarouselProps) {
  const [index, setIndex] = useState(0);

  const hasOverride = Boolean(override.image);
  const safeIndex =
    photos.length > 0 ? ((index % photos.length) + photos.length) % photos.length : 0;
  const activePhoto = photos[safeIndex];

  const resolvedImage = hasOverride ? override.image : activePhoto?.image;
  const resolvedAlt =
    (hasOverride ? override.alt : activePhoto?.alt_text) || fallbackAlt;

  // Only show arrows when we're driving the carousel (no override) and we have
  // more than one photo to cycle between.
  const showArrows = !hasOverride && photos.length > 1;

  const goPrev = (e: React.MouseEvent) => {
    // Don't let the click bubble up to the EditableSlotGroup and open the
    // media picker — the arrow is chrome, not an edit affordance.
    e.stopPropagation();
    setIndex((i) => (i - 1 + photos.length) % photos.length);
  };
  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % photos.length);
  };

  return (
    <>
      {resolvedImage ? (
        <VibesitesImage
          url={resolvedImage}
          alt={resolvedAlt}
          sizes="(min-width: 1024px) 40rem, 100vw"
          className="amenities-row-image"
        />
      ) : (
        <div className="amenities-row-image amenities-row-image--empty" />
      )}

      {showArrows && (
        <>
          <button
            type="button"
            className="amenities-carousel-arrow amenities-carousel-arrow--prev"
            aria-label="Previous photo"
            onClick={goPrev}
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="amenities-carousel-arrow amenities-carousel-arrow--next"
            aria-label="Next photo"
            onClick={goNext}
          >
            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
          </button>
        </>
      )}
    </>
  );
}

// ============================================================================
// Presentational row — heading + subtitle + scrollable bulleted list + CTA on
// one side, featured image on the other. Alternates image side row-by-row.
// ============================================================================

interface AmenitiesRowProps {
  heading: ReactNode;
  subtitle: ReactNode;
  items: Amenity[];
  image: ReactNode;
  cta: ReactNode;
  imageOnLeft?: boolean;
  /** When stacked (tablet + narrower), render the image above the content column. */
  imageAboveOnTablet?: boolean;
  fallbackAlt: string;
  variant?: 'dark' | 'light';
}

function AmenitiesRow({
  heading,
  subtitle,
  items,
  image,
  cta,
  imageOnLeft = false,
  imageAboveOnTablet = false,
  fallbackAlt,
  variant = 'dark',
}: AmenitiesRowProps) {
  const rowRef = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={rowRef}
      className="reveal-fade-up amenities-row"
      data-variant={variant}
      data-image-left={imageOnLeft ? 'true' : 'false'}
      data-image-above-tablet={imageAboveOnTablet ? 'true' : 'false'}
    >
      <div className="amenities-row-content">
        {heading}
        {subtitle}

        <ul className="amenities-row-list">
          {items.map((item, i) => (
            <li key={`${item.title || 'item'}-${i}`} className="amenities-row-item">
              <span className="amenities-row-bullet" aria-hidden="true" />
              <span className="amenities-row-item-label">
                {item.title || fallbackAlt}
              </span>
            </li>
          ))}
        </ul>

        <div className="amenities-row-cta-wrap">{cta}</div>
      </div>

      <div className="amenities-row-media">{image}</div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communitySubtitle = useContentToken('amenitieslist.communitySubtitle');
  const apartmentSubtitle = useContentToken('amenitieslist.apartmentSubtitle');

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityImage = useSlotGroup('amenitieslist.communityImage', communityImageDef);
  const apartmentImage = useSlotGroup('amenitieslist.apartmentImage', apartmentImageDef);

  const communityCta = useSlotGroup('amenitieslist.communityCta', communityCtaDef);
  const apartmentCta = useSlotGroup('amenitieslist.apartmentCta', apartmentCtaDef);

  // Pull the full property photo gallery so we can pick classified fallbacks
  // for the two image slots. The runtime `filter` on collection bindings does
  // only strict equality, so classification (which is a substring match on
  // photo.title via classifyPhoto) has to happen client-side.
  const galleryPhotos = useCollectionBinding<GalleryImage>('amenitieslist.galleryImages', {
    collection: 'photo_gallery',
  });

  const communityPhotos = useMemo(
    () => galleryPhotos.filter((p) => p.image && classifyPhoto(p) === 'community'),
    [galleryPhotos],
  );
  const apartmentPhotos = useMemo(
    () => galleryPhotos.filter((p) => p.image && classifyPhoto(p) === 'apartment'),
    [galleryPhotos],
  );

  useRevealStyles();
  useSiteCtaStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="amenities-section bg-[var(--vibe-color-background)]">
      {hasContent ? (
        <>
          {communityItems.length > 0 && (
            <div className="amenities-band amenities-band--dark">
              <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 md:py-20 lg:px-10">
                <AmenitiesRow
                  variant="dark"
                  imageOnLeft={false}
                  imageAboveOnTablet
                  fallbackAlt="Community amenity"
                  heading={
                    <RichTextContent
                      path="amenitieslist.communityHeading"
                      label="Community Heading"
                      defaultValue="Community amenities"
                      as="h2"
                      className="amenities-row-heading"
                    />
                  }
                  subtitle={
                    <RichTextContent
                      path="amenitieslist.communitySubtitle"
                      label="Community Subtitle"
                      defaultValue="<p>Enjoy access to student-focused community amenities just steps from campus.</p>"
                      as="div"
                      className="amenities-row-subtitle"
                    />
                  }
                  items={communityItems}
                  image={
                    <EditableSlotGroup
                      groupPath="amenitieslist.communityImage"
                      definition={communityImageDef}
                      className="amenities-row-image-slot"
                    >
                      <AmenitiesImageCarousel
                        photos={communityPhotos}
                        override={{ image: communityImage.image, alt: communityImage.alt }}
                        fallbackAlt={communitySubtitle || 'Community amenities'}
                      />
                    </EditableSlotGroup>
                  }
                  cta={
                    <EditableSlotGroup
                      groupPath="amenitieslist.communityCta"
                      definition={communityCtaDef}
                    >
                      <a
                        href={communityCta.url || '/gallery'}
                        target={communityCta._target || '_self'}
                        className="site-cta site-cta--primary"
                      >
                        {communityCta.label || 'View Gallery'}
                      </a>
                    </EditableSlotGroup>
                  }
                />
              </div>
            </div>
          )}

          {apartmentItems.length > 0 && (
            <div className="amenities-band amenities-band--light">
              <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 md:py-20 lg:px-10">
                <AmenitiesRow
                  variant="light"
                  imageOnLeft={true}
                  imageAboveOnTablet
                  fallbackAlt="Apartment feature"
                  heading={
                    <RichTextContent
                      path="amenitieslist.apartmentHeading"
                      label="Apartment Heading"
                      defaultValue="Apartment features"
                      as="h2"
                      className="amenities-row-heading"
                    />
                  }
                  subtitle={
                    <RichTextContent
                      path="amenitieslist.apartmentSubtitle"
                      label="Apartment Subtitle"
                      defaultValue="<p>Every apartment comes thoughtfully finished with the everyday features that make it easy to feel at home.</p>"
                      as="div"
                      className="amenities-row-subtitle"
                    />
                  }
                  items={apartmentItems}
                  image={
                    <EditableSlotGroup
                      groupPath="amenitieslist.apartmentImage"
                      definition={apartmentImageDef}
                      className="amenities-row-image-slot"
                    >
                      <AmenitiesImageCarousel
                        photos={apartmentPhotos}
                        override={{ image: apartmentImage.image, alt: apartmentImage.alt }}
                        fallbackAlt={apartmentSubtitle || 'Apartment features'}
                      />
                    </EditableSlotGroup>
                  }
                  cta={
                    <EditableSlotGroup
                      groupPath="amenitieslist.apartmentCta"
                      definition={apartmentCtaDef}
                    >
                      <a
                        href={apartmentCta.url || '/floor-plans'}
                        target={apartmentCta._target || '_self'}
                        className="site-cta site-cta--primary"
                      >
                        {apartmentCta.label || 'View Floor Plans'}
                      </a>
                    </EditableSlotGroup>
                  }
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 md:py-20 lg:px-10">
          <div className="py-12 text-center text-primary-foreground opacity-70">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        </div>
      )}

      <style>{`
        /* Full-bleed color band that surrounds each row. Community = dark, apartment = light. */
        .amenities-band {
          width: 100%;
        }
        .amenities-band--dark {
          background: var(--vibe-color-primary);
        }
        .amenities-band--light {
          background: var(--vibe-color-background, #ffffff);
        }

        /* Row layout — text on one side, image on the other, stacks on mobile.
           Columns are sized so the image side is always the wider (6fr) column,
           whether the image sits on the right (default) or on the left. */
        .amenities-row {
          display: grid;
          grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
          gap: 3rem;
          align-items: center;
          /* variant-driven colors — overridden per data-variant */
          --amenities-row-fg: var(--vibe-color-text-on-primary, #ffffff);
          --amenities-row-divider: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 25%, transparent);
        }
        /* Community row (primary bg) — remap the shared .site-cta accents
           so the outline reads against the tan fill. */
        .amenities-row[data-variant="dark"] {
          --amenities-row-fg: var(--vibe-color-text-on-primary, #ffffff);
          --amenities-row-divider: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 25%, transparent);
          --site-cta-primary-accent: var(--vibe-color-text-on-primary, #ffffff);
          --site-cta-primary-on-accent: var(--vibe-color-primary);
        }
        .amenities-row[data-variant="light"] {
          --amenities-row-fg: var(--vibe-color-primary);
          --amenities-row-divider: color-mix(in srgb, var(--vibe-color-primary) 18%, transparent);
        }
        /* When the image is on the left, swap the column widths so the
           media column (now in position 1 via order:1) stays at 6fr and the
           content column drops to 5fr. This keeps both images the same width. */
        .amenities-row[data-image-left="true"] {
          grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
        }
        .amenities-row[data-image-left="true"] .amenities-row-content {
          order: 2;
        }
        .amenities-row[data-image-left="true"] .amenities-row-media {
          order: 1;
        }
        @media (max-width: 900px) {
          .amenities-row,
          .amenities-row[data-image-left="true"] {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          .amenities-row[data-image-left="true"] .amenities-row-content,
          .amenities-row[data-image-left="true"] .amenities-row-media {
            order: initial;
          }
          /* Rows opted into image-above-on-tablet stack image above the content
             column once we drop to a single column. */
          .amenities-row[data-image-above-tablet="true"] .amenities-row-media {
            order: 1;
          }
          .amenities-row[data-image-above-tablet="true"] .amenities-row-content {
            order: 2;
          }
        }

        /* ── Left column: heading, subtitle, bullet list, CTA pill ── */
        .amenities-row-content {
          display: flex;
          flex-direction: column;
          min-width: 0;
          color: var(--amenities-row-fg);
        }
        .amenities-row-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4vw, 3.25rem);
          font-weight: 700;
          line-height: 1.1;
          margin: 0 0 1rem;
          color: inherit;
        }
        .amenities-row-subtitle {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.55;
          margin: 0 0 2rem;
          max-width: 32rem;
          color: inherit;
          opacity: 0.92;
        }
        .amenities-row[data-variant="light"] .amenities-row-subtitle {
          opacity: 1;
          color: color-mix(in srgb, var(--vibe-color-primary) 92%, black);
        }
        .amenities-row-subtitle [data-vibesite-richtext] p {
          margin: 0 0 0.75em;
        }
        .amenities-row-subtitle [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }

        /* Bulleted list with thin dividers between rows. Scrolls vertically
           when the amenity count exceeds the visible area — mirrors the POI
           panel on the Neighborhood Map. */
        .amenities-row-list {
          list-style: none;
          padding: 0 0.75rem 0 0;
          margin: 0 0 2.25rem;
          display: flex;
          flex-direction: column;
          max-height: 22rem;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: var(--amenities-row-divider) transparent;
        }
        .amenities-row-list::-webkit-scrollbar { width: 4px; }
        .amenities-row-list::-webkit-scrollbar-track { background: transparent; }
        .amenities-row-list::-webkit-scrollbar-thumb {
          background: var(--amenities-row-divider);
          border-radius: 2px;
        }
        .amenities-row-item {
          display: flex;
          align-items: center;
          gap: 0.9rem;
          padding: 0.9rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.4;
          color: inherit;
          border-bottom: 1px solid var(--amenities-row-divider);
        }
        .amenities-row-item:last-child {
          border-bottom: none;
        }
        .amenities-row-bullet {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--amenities-row-fg);
        }
        .amenities-row-item-label {
          flex: 1;
          min-width: 0;
        }

        /* Site-wide .site-cta pill sits inside this wrapper. */
        .amenities-row-cta-wrap {
          display: block;
        }
        @media (max-width: 900px) {
          .amenities-row-cta-wrap {
            display: flex;
            justify-content: center;
          }
        }

        /* ── Right column: single featured image ── */
        .amenities-row-media {
          position: relative;
          width: 100%;
          isolation: isolate;
        }
        .amenities-row-image-slot {
          display: block;
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
        }
        .amenities-row-image {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .amenities-row-image--empty {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 12%, transparent);
        }

        /* ── Hover-visible prev/next arrows for cycling classified photos ──
           Style: standalone white chevron glyphs, no background or border.
           A subtle drop-shadow keeps them legible against light photos.
           Slightly translucent by default, fully opaque on direct hover. */
        .amenities-carousel-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #ffffff;
          font-size: 2.5rem;
          line-height: 1;
          cursor: pointer;
          filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
          opacity: 0;
          transition: opacity 0.18s ease, transform 0.18s ease;
        }
        .amenities-carousel-arrow--prev {
          left: 0.75rem;
        }
        .amenities-carousel-arrow--next {
          right: 0.75rem;
        }
        /* Reveal on hover of the image slot at a translucent state; ramp to
           full opacity when the arrow itself is directly hovered/focused. */
        .amenities-row-image-slot:hover .amenities-carousel-arrow {
          opacity: 0.7;
        }
        .amenities-row-image-slot:hover .amenities-carousel-arrow:hover,
        .amenities-carousel-arrow:focus-visible {
          opacity: 1;
        }
        .amenities-carousel-arrow:active {
          transform: translateY(-50%) scale(0.94);
        }
        .amenities-carousel-arrow:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 4px;
          border-radius: 4px;
        }
        /* Touch devices don't hover — reveal the arrows persistently at a
           subtler opacity so they're still discoverable. */
        @media (hover: none) {
          .amenities-carousel-arrow {
            opacity: 0.75;
          }
        }

        @media (max-width: 640px) {
          .amenities-row-heading {
            font-size: 2rem;
          }
          .amenities-row-item {
            padding: 0.75rem 0;
            font-size: 0.9375rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: 'Community amenities',
    },
    'amenitieslist.communitySubtitle': {
      label: 'Community Subtitle',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
      defaultValue:
        '<p>Enjoy access to student-focused community amenities just steps from campus.</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: 'Apartment features',
    },
    'amenitieslist.apartmentSubtitle': {
      label: 'Apartment Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Every apartment comes thoughtfully finished with the everyday features that make it easy to feel at home.</p>',
    },
  },
  slotGroups: {
    'amenitieslist.communityImage': communityImageDef,
    'amenitieslist.communityCta': communityCtaDef,
    'amenitieslist.apartmentImage': apartmentImageDef,
    'amenitieslist.apartmentCta': apartmentCtaDef,
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.galleryImages': {
      label: 'Property Photos',
      collection: 'photo_gallery',
      description:
        'Source pool for the community and apartment image slots. Each slot picks the first photo whose title identifies it as community or apartment; site owners can override either slot with a specific image.',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
